import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import type {
  ActionAppointment,
  ActionClient,
  ActionDialogRequest,
} from "./actions/action-types";
import { CancelDialog } from "./actions/cancel-dialog";
import { DeleteDialog } from "./actions/delete-dialog";
import { EditDetailsDialog } from "./actions/edit-details-dialog";
import { NewAppointmentDialog } from "./actions/new-appointment-dialog";
import { RescheduleDialog } from "./actions/reschedule-dialog";

// NO-876. The five appointment-action dialogs behind one host. The Week view
// (NO-874) and the details panel (NO-875) open them through the callbacks
// below and refresh when `onChanged` fires.

export interface AppointmentActionDialogsController {
  request: ActionDialogRequest | null;
  close: () => void;
  setRequest: (request: ActionDialogRequest | null) => void;
  /** Header "New appointment" and the details panel's "Book next visit". */
  openNew: (client?: ActionClient, returnFocusToAppointmentId?: string) => void;
  openReschedule: (appointment: ActionAppointment) => void;
  openEdit: (appointment: ActionAppointment) => void;
  openCancelOrMarkMissed: (appointment: ActionAppointment) => void;
  openDelete: (appointment: ActionAppointment) => void;
}

interface ReturnTarget {
  /** The visit's Week block, looked up again when focus returns: a refreshed week may have replaced it. */
  blockId: string | null;
  /** The control that was focused, used when there is no block. */
  element: HTMLElement | null;
}

/**
 * Where focus returns when the dialog closes: the visit's block on the Week
 * grid when there is one (the details sheet that held the button is gone by
 * then), otherwise the control that was focused.
 */
function targetFor(request: ActionDialogRequest): ReturnTarget {
  const blockId =
    (request.kind === "new"
      ? request.returnFocusToAppointmentId
      : request.appointment.id) ?? null;
  const active = document.activeElement;

  return {
    blockId,
    element: active instanceof HTMLElement && active !== document.body ? active : null,
  };
}

interface ResolvedTarget {
  element: HTMLElement;
  /** The week's first block, a stand-in: focus it without scrolling the grid to it. */
  standIn: boolean;
}

function resolveTarget(target: ReturnTarget): ResolvedTarget | null {
  const block = target.blockId
    ? document.querySelector(`[data-appointment-id="${CSS.escape(target.blockId)}"]`)
    : null;

  if (block instanceof HTMLElement) return { element: block, standIn: false };

  if (target.element?.isConnected) return { element: target.element, standIn: false };

  // The visit is gone (deleted, or moved out of this week) and the sheet that
  // held the button has closed: land on the week's first block, not the page.
  const anyBlock = document.querySelector("[data-appointment-id]");

  return anyBlock instanceof HTMLElement ? { element: anyBlock, standIn: true } : null;
}

/** The RD taking over ends the focus retries: a click, a key, a touch or a scroll. */
const RD_INPUT_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart"] as const;

const FOCUS_RETRY_DELAYS_MS = [800, 2500, 6000] as const;

/**
 * Focus goes back to where the RD was. It tries once the dialog is gone and
 * again while the week refreshes, whenever focus has fallen to the page. The
 * returned function stops it; the RD's own click or key press does too.
 */
function restoreFocus(target: ReturnTarget | null): () => void {
  if (!target) return () => undefined;

  const focus = (): void => {
    const found = resolveTarget(target);

    found?.element.focus({ preventScroll: found.standIn });
  };

  const timers = [
    setTimeout(focus, 0),
    ...FOCUS_RETRY_DELAYS_MS.map((delayMs) =>
      setTimeout(() => {
        if (document.activeElement === document.body) focus();
      }, delayMs)
    ),
  ];

  const stop = (): void => {
    timers.forEach(clearTimeout);
    for (const type of RD_INPUT_EVENTS) document.removeEventListener(type, stop, true);
  };

  for (const type of RD_INPUT_EVENTS) document.addEventListener(type, stop, true);

  return stop;
}

export function useAppointmentActionDialogs(): AppointmentActionDialogsController {
  const [request, setRequest] = useState<ActionDialogRequest | null>(null);
  const opener = useRef<ReturnTarget | null>(null);
  const stopFocus = useRef<() => void>(() => undefined);
  const open = useCallback((next: ActionDialogRequest) => {
    stopFocus.current();
    opener.current = targetFor(next);
    setRequest(next);
  }, []);
  const close = useCallback(() => {
    setRequest(null);
    stopFocus.current();
    stopFocus.current = restoreFocus(opener.current);
    opener.current = null;
  }, []);

  useEffect(() => () => stopFocus.current(), []);

  return {
    request,
    close,
    setRequest,
    openNew: (client, returnFocusToAppointmentId) =>
      open({ kind: "new", client, returnFocusToAppointmentId }),
    openReschedule: (appointment) => open({ kind: "reschedule", appointment }),
    openEdit: (appointment) => open({ kind: "edit", appointment }),
    openCancelOrMarkMissed: (appointment) => open({ kind: "cancel", appointment }),
    openDelete: (appointment) => open({ kind: "delete", appointment }),
  };
}

/** Renders its children only while the `rd-schedule` flag reads on. */
export function RdScheduleFlagGate(props: {
  enabled: boolean;
  children: ReactNode;
}): ReactElement | null {
  return props.enabled ? <>{props.children}</> : null;
}

export interface AppointmentActionDialogsProps {
  controller: AppointmentActionDialogsController;
  rdId: string | null;
  timeZone: string;
  /** An action went through: refresh the week. */
  onChanged: () => void;
  /**
   * A dialog opened from the details panel closed with nothing changed (Back,
   * Keep appointment, Close or Escape): reopen that visit's panel.
   */
  onBackToDetails?: (appointmentId: string) => void;
}

/**
 * The visit whose details panel opened a request. Every request for one visit
 * comes from that panel's footer, and a New appointment that names a visit
 * came from its "Book next visit"; the header's New appointment names none.
 */
function detailsOrigin(request: ActionDialogRequest): string | null {
  return request.kind === "new"
    ? (request.returnFocusToAppointmentId ?? null)
    : request.appointment.id;
}

/**
 * A dialog opened from the details panel returns to that panel when it closes
 * with nothing changed, and also after a saved edit: the panel is where the
 * saved note shows. The other actions that changed something close for good
 * (the visit moved, ended or went away).
 */
export function returnsToDetails(
  request: ActionDialogRequest,
  changed: boolean
): boolean {
  return !changed || request.kind === "edit";
}

export function UngatedAppointmentActionDialogs(
  props: AppointmentActionDialogsProps
): ReactElement | null {
  const { controller, rdId, timeZone, onChanged, onBackToDetails } = props;
  const { request } = controller;
  // The request that changed something; a later request starts unchanged.
  const changed = useRef<ActionDialogRequest | null>(null);

  if (!request) return null;

  const shared = {
    rdId,
    timeZone,
    open: true,
    onChanged: () => {
      changed.current = request;
      onChanged();
    },
    onOpenChange: (open: boolean) => {
      if (open) return;

      const origin = detailsOrigin(request);

      if (
        returnsToDetails(request, changed.current === request) &&
        origin !== null &&
        onBackToDetails
      ) {
        // The panel takes focus as it opens, so nothing is restored to the grid.
        controller.setRequest(null);
        onBackToDetails(origin);

        return;
      }

      controller.close();
    },
  };

  if (request.kind === "new") {
    return <NewAppointmentDialog key="new" {...shared} client={request.client} />;
  }

  const { appointment } = request;
  const key = `${request.kind}-${appointment.id}`;
  const switchTo = (kind: "cancel") => controller.setRequest({ kind, appointment });

  switch (request.kind) {
    case "reschedule":
      return <RescheduleDialog key={key} {...shared} appointment={appointment} />;
    case "edit":
      return <EditDetailsDialog key={key} {...shared} appointment={appointment} />;
    case "cancel":
      return <CancelDialog key={key} {...shared} appointment={appointment} />;
    case "delete":
      return (
        <DeleteDialog
          key={key}
          {...shared}
          appointment={appointment}
          onCancelInstead={() => switchTo("cancel")}
        />
      );
  }
}
